import React, { FC } from 'react';

import styled from 'styled-components';
import { FiPlusCircle, FiTrash2 } from 'react-icons/fi';
import { Trans, useTranslation } from 'react-i18next';

import { Flex } from '@resideo/blueprint-react';

import Typography from 'components/common/Typography';
import { Link } from 'react-router-dom';

const { Text } = Typography;

export const DATA_EXAMPLE = `{
  "DeviceId": "48A2E60XXXXX",
  "Timestamp": "2025-06-05T20:39:10Z",
  "MessageType": "DeviceFirmwareUpgradeQuery",
  "Message": {
    "QueryMessage": {
      "DeviceId": "48A2E60XXXXX",
      "HardwareRevision": "JNATRNC1746-03-F",
      "OSIndex": 2816,
      "DeviceType": 256,
      "InstalledStatus": {
        "Guid": "c2c3a7a3-648b-41e4-afd6-6ea0c44110d8",
        "CFVersion": "4.4.2.80",
        "GlobalStatus": "VALID",
        "Modules": [
          {
            "ModuleId": 272,
            "Status": "VALID",
            "Version": "5.0.4.70",
            "ProvTimeVal": 7,
            "WDGRSTval": 0,
            "WDGRSTlimit": 6,
            "HardwareRevision": "JNATRNC1746-03-F"
          }
        ]
      }
    },
    "FirmwareId": "",
    "ClientId": "Analytics",
    "ChannelId": "Analytics",
    "OccurenceTime": "2025-06-05T20:39:04.4487989Z"
  }
}`;

export const AddIcon = styled(FiPlusCircle)`
  margin-inline-end: 8px;
  font-size: 24px;
`;

export const RemoveIcon = styled(FiTrash2)`
  font-size: 24px;
`;

export const CodeBlock = styled.pre`
  background: #d9d9d9;
  border: thin solid black;
  padding-inline: 1rem;
  padding-block: 1rem;
`;

export const DeviceEntryContainer = styled(Flex)`
  border: thin solid #e1e1e2;
  padding: 12px;
  margin-block: 20px;
`;

export const OrderedList = styled.ol`
  padding-inline-start: 1.5rem;
  padding-block-start: 0.5rem;
  margin: 0;

  li {
    font-size: 1rem;
    margin-bottom: 0.5rem;
    white-space: pre-line;
  }
`;

export const PreLineText = styled(Text)`
  font-size: 1rem;
  text-align: justify;
  white-space: pre-line;
`;

export const ScrollableArea = styled.div`
  max-height: 70vh;
  overflow: auto;
`;

export const DataStreamIntroduction: FC = () => {
  const { t } = useTranslation();

  const components = {
    codeExample: <CodeBlock>{DATA_EXAMPLE}</CodeBlock>,
    mailTo: <a href='mailto:privacy@resideo.com' />,
    linkTo: (
      <Link
        to='https://www.resideo.com/us/en/corporate/legal/terms-and-conditions/en-us/'
        target='_blank'
      />
    ),
  };

  const prerequisites = [
    'privacy.dataStream.introduction.prerequisites.webhook',
    'privacy.dataStream.introduction.prerequisites.https',
    'privacy.dataStream.introduction.prerequisites.tls',
    'privacy.dataStream.introduction.prerequisites.format',
    'privacy.dataStream.introduction.prerequisites.headers',
  ];

  const notes = [
    'privacy.dataStream.introduction.notes.noAlerting',
    'privacy.dataStream.introduction.notes.failurePolicy',
    'privacy.dataStream.introduction.notes.timeout',
    'privacy.dataStream.introduction.notes.forgottenRequest',
    'privacy.dataStream.introduction.notes.sensitiveData',
  ];

  return (
    <Flex
      data-test-data-stream-introduction
      as='section'
      flexDirection='column'
      style={{ gap: '0.5rem' }}>
      <PreLineText>{t('privacy.dataStream.introduction.title')}</PreLineText>

      <PreLineText>
        {t('privacy.dataStream.introduction.description')}
      </PreLineText>

      <PreLineText>
        {t('privacy.dataStream.introduction.prerequisites.title')}
      </PreLineText>
      <OrderedList data-test-data-stream-prerequisites>
        {prerequisites.map(prerequisite => (
          <li key={prerequisite}>
            <Trans i18nKey={prerequisite} components={components} />
          </li>
        ))}
      </OrderedList>

      <PreLineText>
        {t('privacy.dataStream.introduction.notes.title')}
      </PreLineText>
      <OrderedList data-test-data-stream-notes>
        {notes.map(note => (
          <li key={note}>
            <Trans i18nKey={note} components={components} />
          </li>
        ))}
      </OrderedList>

      <PreLineText>
        <Trans
          i18nKey='privacy.dataStream.introduction.authorization'
          components={components}
        />
      </PreLineText>
    </Flex>
  );
};

export const DataStreamRequirements: FC = () => {
  const { t } = useTranslation();

  const requirements = [
    t('privacy.dataStream.setup.requirements.webhook'),
    t('privacy.dataStream.setup.requirements.httpsSupport'),
    t('privacy.dataStream.setup.requirements.tlsVersion'),
    t('privacy.dataStream.setup.requirements.dataFormat'),
  ];

  return (
    <Flex data-test-data-stream-description as='section' flexDirection='column'>
      <Text data-test-data-stream-intro>
        {t('privacy.dataStream.setup.requirements.intro')}
      </Text>
      <OrderedList data-test-data-stream-requirements>
        {requirements.map(req => (
          <li key={req}>{req}</li>
        ))}
      </OrderedList>
    </Flex>
  );
};
